import React, { useState, useRef, useEffect } from 'react';

/**
 * Unified Export Dropdown Component
 * Single button with all export options for maximum space efficiency and mobile friendliness
 * Replaces all individual export buttons across the application
 */
const UnifiedExportDropdown = ({ 
  onExportPDF,
  onExportExcel,
  onExportImage,
  onPrint,
  onShareLink,
  onSharePDF,
  onShareExcel,
  onShareImage,
  disabled = false,
  className = '',
  size = 'default' // 'small', 'default', 'large'
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('right');
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const lastClickTime = useRef(0);

  // Size configurations
  const sizeConfig = {
    small: {
      button: 'px-2 py-1.5 text-xs',
      icon: 'w-3 h-3',
      text: 'text-xs',
      dropdown: 'w-18'
    },
    default: {
      button: 'px-3 py-2 text-sm',
      icon: 'w-4 h-4',
      text: 'text-sm',
      dropdown: 'w-22'
    },
    large: {
      button: 'px-4 py-2.5 text-base',
      icon: 'w-5 h-5',
      text: 'text-base',
      dropdown: 'w-26'
    }
  };

  const config = sizeConfig[size];

  // Smart positioning logic
  const calculateDropdownPosition = () => {
    if (!buttonRef.current) return 'right';
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const screenWidth = window.innerWidth;
    const dropdownWidth = size === 'small' ? 100 : size === 'large' ? 120 : 100;
    
    // Check if dropdown would overflow on the right
    const rightSpace = screenWidth - buttonRect.right;
    const leftSpace = buttonRect.left;
    
    if (rightSpace < dropdownWidth && leftSpace > dropdownWidth) {
      return 'left';
    }
    return 'right';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update position when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      setDropdownPosition(calculateDropdownPosition());
    }
  }, [showDropdown]);

  const handleOptionClick = async (action) => {
    if (!action || isProcessing || disabled) return;
    
    const now = Date.now();
    if (now - lastClickTime.current < 500) return; // Prevent rapid clicks
    lastClickTime.current = now;
    
    setShowDropdown(false);
    setIsProcessing(true);
    
    try {
      if (typeof action === 'function') {
        await action();
      }
    } catch (error) {
      console.error('Export action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Organize export options in logical groups
  const exportOptions = [
    // Primary Export Options
    { 
      label: '📄 PDF', 
      action: onExportPDF,
      disabled: !onExportPDF,
      category: 'export'
    },
    { 
      label: '📊 Excel', 
      action: onExportExcel,
      disabled: !onExportExcel,
      category: 'export'
    },
    { 
      label: '🖼️ Image', 
      action: onExportImage,
      disabled: !onExportImage,
      category: 'export'
    },
    { 
      label: '🖨️ Print', 
      action: onPrint,
      disabled: !onPrint,
      category: 'export'
    },
    // Share Options
    { 
      label: '📤 Share PDF', 
      action: onSharePDF,
      disabled: !onSharePDF,
      category: 'share'
    },
    { 
      label: '📤 Share Excel', 
      action: onShareExcel,
      disabled: !onShareExcel,
      category: 'share'
    },
    { 
      label: '📤 Share Image', 
      action: onShareImage,
      disabled: !onShareImage,
      category: 'share'
    },
    { 
      label: '📱 Share Link', 
      action: onShareLink,
      disabled: !onShareLink,
      category: 'share'
    }
  ].filter(option => !option.disabled);

  // Group options by category
  const exportGroup = exportOptions.filter(opt => opt.category === 'export');
  const shareGroup = exportOptions.filter(opt => opt.category === 'share');

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Unified Export Button */}
      <button
        ref={buttonRef}
        onClick={() => setShowDropdown(!showDropdown)}
        disabled={disabled || isProcessing}
        className={`flex items-center justify-center gap-2 ${config.button} rounded-md text-white transition-all duration-200 font-medium ${
          disabled 
            ? 'bg-gray-400 cursor-not-allowed' 
            : isProcessing
            ? 'bg-blue-400 cursor-wait'
            : 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700 shadow-sm hover:shadow-md'
        }`}
        title="Export & Share Options"
      >
        {isProcessing ? (
          <div className={`${config.icon} border-2 border-white border-t-transparent rounded-full animate-spin`}></div>
        ) : (
          <>
            <svg className={config.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className={`${config.text} hidden sm:inline`}>Export</span>
            <svg className={`${config.icon} ${config.text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {/* Smart Positioned Dropdown Menu */}
      {showDropdown && (
        <div className={`absolute top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1 min-w-[150px] max-w-[90vw] max-h-[80vh] overflow-auto ${
          dropdownPosition === 'left' ? 'right-0' : 'left-0'
        }`}>
          {exportGroup.length > 0 && (
            <>
              <div className="px-2 py-1 text-xs font-semibold text-gray-500 border-b border-gray-100">
                Export
              </div>
              {exportGroup.map((option, index) => (
                <button
                  key={`export-${index}`}
                  onClick={() => handleOptionClick(option.action)}
                  disabled={option.disabled || isProcessing}
                  className={`w-full text-left px-2 py-1.5 ${config.text} transition-colors ${
                    option.disabled || isProcessing
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </>
          )}

          {shareGroup.length > 0 && (
            <>
              {exportGroup.length > 0 && <div className="border-t border-gray-100 my-1"></div>}
              <div className="px-2 py-1 text-xs font-semibold text-gray-500 border-b border-gray-100">
                Share
              </div>
              {shareGroup.map((option, index) => (
                <button
                  key={`share-${index}`}
                  onClick={() => handleOptionClick(option.action)}
                  disabled={option.disabled || isProcessing}
                  className={`w-full text-left px-2 py-1.5 ${config.text} transition-colors ${
                    option.disabled || isProcessing
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </>
          )}

          {exportOptions.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-gray-500 text-center">
              No export options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UnifiedExportDropdown;
